package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.schedule.CopySource
import com.evoapps.evoshift.domain.schedule.RosterCopyMapping
import com.evoapps.evoshift.domain.usecase.roster.CopyPreview
import com.evoapps.evoshift.domain.usecase.roster.PlannedDaysMode
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.number
import kotlinx.datetime.plus

/** SH-205: the "Copy shifts" tool's two steps: where from (Skąd), then where to (Dokąd). */
/** SH-229: where to's main button: what is still to pick, or Copy. */
enum class CopyToolAction { PICK_DAYS, PICK_START, COPY }

enum class CopyToolStep { SOURCE, TARGET }

/**
 * SH-205: the one "Copy shifts" tool (it replaces SH-114's "Copy from…" and SH-171's "Copy days")
 * while it is open. Where from ([sources]): a day, several days, a week or any range, also in an
 * earlier month ([shownYear]/[shownMonth] is the source calendar's month). Where to: for one day,
 * the days it is copied onto ([starts], with quick picks); for a block of days, the day it starts
 * on ([starts], usually one) and whether it repeats back to back until the month ends ([repeat]).
 * Then SH-199's Skip, Add or Replace and the preview (CopyShiftsUseCase.preview with
 * [CopySource.Days]). Nothing is written until the manager copies. Pure: the view model applies
 * the changes below and works out the preview.
 */
data class CopyToolUiState(
    /** The roster's month: the only month copies go into. */
    val year: Int,
    val month: Int,
    /** The workplace's first weekday (ISO 1-7): the calendars' first column, weeks and "Whole week". */
    val firstDayOfWeek: Int = 1,
    val step: CopyToolStep = CopyToolStep.SOURCE,
    val sources: Set<LocalDate> = emptySet(),
    /** The day tapped last in where from: a tap on another day picks the range between them. */
    val rangeAnchor: LocalDate? = null,
    val shownYear: Int = year,
    val shownMonth: Int = month,
    /** How many shifts each day has: the roster's month, and the earlier months once read. */
    val shiftsOn: Map<LocalDate, Int> = emptyMap(),
    /** An earlier month's shifts are being read for the source calendar. */
    val loadingMonth: Boolean = false,
    /** The roster month's closed days (Sunday policy, holidays): struck through. */
    val closedDays: Set<LocalDate> = emptySet(),
    val starts: Set<LocalDate> = emptySet(),
    val repeat: Boolean = false,
    /** SH-199: days that already have shifts: skipped (default), added to or replaced. */
    val plannedDays: PlannedDaysMode = PlannedDaysMode.SKIP,
    val isPreviewing: Boolean = false,
    val preview: CopyPreview? = null,
    /**
     * "Whole previous month": the month before the roster's mapped day by day by weekday (the
     * n-th Monday from the n-th Monday, [CopySource.PreviousMonth]) onto the whole month.
     */
    val wholePreviousMonth: Boolean = false
) {
    companion object {
        /** How many months before the roster's the source may come from. */
        const val MONTHS_BACK = 2

        /** A day's week by the workplace's first weekday: its first day. */
        fun weekStart(day: LocalDate, firstDayOfWeek: Int): LocalDate {
            val first = firstDayOfWeek.takeIf { it in 1..7 } ?: 1
            return day.plus(DatePeriod(days = -((day.dayOfWeek.isoDayNumber - first + 7) % 7)))
        }

        /** The week number (ISO 8601: the week of its Thursday) of the seven days from [start]. */
        fun weekNumber(start: LocalDate): Int {
            val thursday = (0 until 7).map { start.plus(DatePeriod(days = it)) }.first { it.dayOfWeek == DayOfWeek.THURSDAY }
            return (thursday.dayOfYear - 1) / 7 + 1
        }

        private fun monthDays(year: Int, month: Int): List<LocalDate> =
            generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }.takeWhile { it.month.number == month }.toList()
    }

    /** The roster month's days. */
    val days: List<LocalDate> get() = monthDays(year, month)

    /** The source calendar's days. */
    val shownDays: List<LocalDate> get() = monthDays(shownYear, shownMonth)

    val monthEnd: LocalDate get() = days.last()

    private val shownIndex: Int get() = shownYear * 12 + shownMonth
    private val rosterIndex: Int get() = year * 12 + month

    /** The source calendar may go one month back (up to [MONTHS_BACK]) or forward (up to the roster's month). */
    val canShowEarlier: Boolean get() = step == CopyToolStep.SOURCE && shownIndex > rosterIndex - MONTHS_BACK
    val canShowLater: Boolean get() = step == CopyToolStep.SOURCE && shownIndex < rosterIndex

    /** One day is copied onto other days; anything longer is a block pasted from a start day. */
    val isSingleDay: Boolean get() = sources.size == 1 && !wholePreviousMonth

    /** The first source day: the one each start receives. */
    val anchor: LocalDate? get() = sources.minOrNull()

    /** The source days' shifts. */
    val sourceShifts: Int get() = sources.sumOf { shiftsOn[it] ?: 0 }

    /** What is copied where, for the preview (null while there is nothing to preview). */
    val sourceSpec: CopySource?
        get() {
            if (wholePreviousMonth) return CopySource.PreviousMonth
            if (sources.isEmpty() || starts.isEmpty()) return null
            return if (!isSingleDay && repeat) CopySource.repeat(sources, starts.min(), monthEnd) else CopySource.Days(sources, starts)
        }

    /** The days the copies land on (also those past the month, which are not copied). */
    val targets: Set<LocalDate>
        get() = when (val spec = sourceSpec) {
            null -> emptySet()
            is CopySource.Days -> spec.mapping().keys
            else -> RosterCopyMapping.days(spec, year, month).keys
        }

    /** "Every Monday in the month": the month's days on the source day's weekday (not that day). */
    val everyWeekday: Set<LocalDate>
        get() = anchor?.let { a -> days.filter { it.dayOfWeek == a.dayOfWeek && it != a }.toSet() }.orEmpty()

    /** "Every working day": Monday to Friday of the month that are open (not the source day). */
    val workdays: Set<LocalDate>
        get() = days.filter { it.dayOfWeek.isoDayNumber <= 5 && it != anchor && it !in closedDays }.toSet()

    /** "Whole week" (one source day): the other days of its week, in this month. */
    val wholeWeek: Set<LocalDate>
        get() {
            val source = sources.singleOrNull() ?: return emptySet()
            val start = weekStart(source, firstDayOfWeek)
            return (0 until 7).map { start.plus(DatePeriod(days = it)) }.filter { it != source && it.year == year && it.month.number == month }.toSet()
        }

    /**
     * Where a block starts by default: the day after it, when that is in the month; else the
     * month's first day on the block's first weekday (a week from last month lands on the
     * month's first full week), else the month's first day.
     */
    val defaultStart: LocalDate?
        get() {
            val first = sources.minOrNull() ?: return null
            val after = sources.max().plus(DatePeriod(days = 1))
            if (after.year == year && after.month.number == month) return after
            return days.firstOrNull { it.dayOfWeek == first.dayOfWeek && it != first } ?: days.first()
        }

    val canContinue: Boolean get() = step == CopyToolStep.SOURCE && sourceShifts > 0

    /** SH-229: the month's days the copies land on, for "Copy (N days)". */
    val targetDaysInMonth: Int get() = targets.count { it.year == year && it.month.number == month }

    /**
     * SH-229: what where to's main button says: what is still to pick (one day's target days, a
     * block's first day), else "Copy (N days)".
     */
    val copyAction: CopyToolAction
        get() = when {
            wholePreviousMonth || starts.isNotEmpty() -> CopyToolAction.COPY
            isSingleDay -> CopyToolAction.PICK_DAYS
            else -> CopyToolAction.PICK_START
        }

    val canCopy: Boolean
        get() = step == CopyToolStep.TARGET && (starts.isNotEmpty() || wholePreviousMonth) && !isPreviewing && (preview?.shifts?.isNotEmpty() == true)

    /**
     * Step one: a tap on a day picks that day; a tap on another day right after it picks the
     * range between them (also across months). Where to is chosen again for a new source.
     */
    fun tapSource(day: LocalDate): CopyToolUiState {
        if (step != CopyToolStep.SOURCE) return this
        val anchor = rangeAnchor
        if (anchor != null && anchor != day && sources == setOf(anchor)) {
            val (from, to) = if (anchor < day) anchor to day else day to anchor
            return withSources(generateSequence(from) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= to }.toSet()).copy(rangeAnchor = null)
        }
        return withSources(setOf(day)).copy(rangeAnchor = day)
    }

    /** Step one: a tap on a week's number picks its seven days. */
    fun pickWeek(start: LocalDate): CopyToolUiState =
        if (step != CopyToolStep.SOURCE) this else withSources((0 until 7).map { start.plus(DatePeriod(days = it)) }.toSet()).copy(rangeAnchor = null)

    private fun withSources(picked: Set<LocalDate>): CopyToolUiState =
        if (picked == sources && !wholePreviousMonth) this
        else copy(sources = picked, starts = emptySet(), repeat = false, preview = null, isPreviewing = false, wholePreviousMonth = false)

    /** The month before the roster's: its first day. */
    val previousMonthStart: LocalDate get() = LocalDate(year, month, 1).plus(DatePeriod(months = -1))

    /** Step one's preset "Whole previous month": every day of it, copied by weekday onto the whole month. */
    fun pickWholePreviousMonth(): CopyToolUiState {
        if (step != CopyToolStep.SOURCE) return this
        val first = previousMonthStart
        return copy(
            sources = monthDays(first.year, first.month.number).toSet(),
            wholePreviousMonth = true,
            rangeAnchor = null,
            starts = emptySet(),
            repeat = false,
            preview = null,
            isPreviewing = false,
            shownYear = first.year,
            shownMonth = first.month.number
        )
    }

    /** The source calendar one month earlier or later. */
    fun showMonth(delta: Int): CopyToolUiState {
        if ((delta < 0 && !canShowEarlier) || (delta > 0 && !canShowLater) || delta == 0) return this
        val shown = LocalDate(shownYear, shownMonth, 1).plus(DatePeriod(months = delta))
        return copy(shownYear = shown.year, shownMonth = shown.month.number)
    }

    /** On to where to: a block starts at [defaultStart] unless a start is picked already. */
    fun next(): CopyToolUiState {
        if (!canContinue) return this
        val start = if (wholePreviousMonth) emptySet() else if (!isSingleDay && starts.isEmpty()) setOfNotNull(defaultStart) else starts
        return copy(step = CopyToolStep.TARGET, starts = start)
    }

    /** Back to where from (the choice of where to is kept for the same source). */
    fun back(): CopyToolUiState = copy(step = CopyToolStep.SOURCE, isPreviewing = false, preview = null)

    /** Step two: one day: ticks or unticks a target day; a block: starts there (a second tap unpicks it). */
    fun tapTarget(day: LocalDate): CopyToolUiState {
        if (step != CopyToolStep.TARGET || day !in days || wholePreviousMonth) return this
        return when {
            isSingleDay -> copy(starts = if (day in starts) starts - day else starts + day)
            starts == setOf(day) -> copy(starts = emptySet())
            else -> copy(starts = setOf(day))
        }
    }

    /** A quick pick ("every Monday", "every working day", "whole week"): adds its days, or takes them away when all are picked. */
    fun toggleTargets(picks: Set<LocalDate>): CopyToolUiState {
        val valid = picks.filter { it in days }.toSet()
        if (step != CopyToolStep.TARGET || !isSingleDay || valid.isEmpty()) return this
        return copy(starts = if (starts.containsAll(valid)) starts - valid else starts + valid)
    }

    fun clearTargets(): CopyToolUiState = copy(starts = emptySet())

    /** A block: "Repeat until the month ends" on or off. */
    fun withRepeat(on: Boolean): CopyToolUiState = if (isSingleDay || wholePreviousMonth) this else copy(repeat = on)

    /** Whether [other] asks for the same preview as this (a late preview for another choice is dropped). */
    fun samePreviewAs(other: CopyToolUiState): Boolean =
        other.sources == sources && other.starts == starts && other.repeat == repeat && other.wholePreviousMonth == wholePreviousMonth && other.plannedDays == plannedDays && other.step == step
}
